import { Checkbox } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faPlus,
  faSpinner,
  faXmark,
} from "@fortawesome/pro-solid-svg-icons";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import { getUserAuth } from "../../recoil/selectors";
import { TrackAnalyticsEvent } from "../../services/Analytics";
import { themePalette } from "../../theme";
import { AnalyticsEventNames } from "../../utils/analyticsEvents";
import { validateEmail, validateName } from "../../utils/hooks/helpers";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import {
  ShareFetalSexEmailRequest,
  ShareFetalSexEmailResponse,
} from "../DetailedResults/Services/DetailedResultsService";
import { TextField } from "../shared/TextField/TextField";
import { GetTypographyContent } from "../shared/Typography/Typography";
import {
  StandaloneFetalSexShareActionRow,
  StandaloneFetalSexShareAddRecipientRow,
  StandaloneFetalSexShareAuthorizationRow,
  StandaloneFetalSexShareAuthorizationText,
  StandaloneFetalSexShareError,
  StandaloneFetalSexShareModalCard,
  StandaloneFetalSexShareModalHeader,
  StandaloneFetalSexShareRecipientFields,
  StandaloneFetalSexShareRecipientGroup,
  StandaloneFetalSexShareRecipientHeader,
  StandaloneFetalSexShareRemoveButton,
  StandaloneFetalSexShareSuccessActionRow,
  StandaloneFetalSexShareSuccessIcon,
  StandaloneFetalSexShareSuccessMessage,
  StandaloneFetalSexShareSubmitButton,
  StandaloneFetalSexShareTextButton,
} from "./StandaloneFetalSexShareModal.styles";

interface StandaloneFetalSexShareModalProps {
  onClose: () => void;
  onRequestInFlightChange: (isInFlight: boolean) => void;
  requisitionID: string;
}

interface RecipientDraft {
  email: string;
  emailError: string;
  id: number;
  name: string;
  nameError: string;
}

const MODAL_ANALYTICS_CONTEXT = {
  modal_name: "share_fetal_sex",
  page_name: "LandingPage",
};

export const MAX_SHARE_FETAL_SEX_RECIPIENTS = 20;
export const MAX_SHARE_FETAL_SEX_RECIPIENT_NAME_LENGTH = 100;
const PRIVACY_PRACTICES_URL = "https://billiontoone.com/privacy-practices/";
const SHARE_AUTHORIZATION_ID = "share-fetal-sex-authorization";

export const StandaloneFetalSexShareModal = React.forwardRef<
  HTMLDivElement,
  StandaloneFetalSexShareModalProps
>(({ onClose, onRequestInFlightChange, requisitionID }, ref) => {
  const { t } = useTranslation();
  const auth = useRecoilValue(getUserAuth);
  const { deviceType, isMobile } = useDeviceHook();
  const nextRecipientIdRef = useRef(1);

  const createEmptyRecipient = (): RecipientDraft => {
    const id = nextRecipientIdRef.current;
    nextRecipientIdRef.current += 1;
    return { email: "", emailError: "", id, name: "", nameError: "" };
  };

  const [recipients, setRecipients] = useState<RecipientDraft[]>(() => [
    createEmptyRecipient(),
  ]);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [phase, setPhase] = useState<"form" | "success">("form");
  const [sentEmails, setSentEmails] = useState<string[]>([]);
  const [responseMessage, setResponseMessage] = useState("");

  useEffect(() => {
    if (auth?.access_token) {
      TrackAnalyticsEvent(
        AnalyticsEventNames.impression,
        auth?.access_token,
        auth?.user_id,
        {
          impression_type: "modal_view",
          ...MODAL_ANALYTICS_CONTEXT,
        },
      );
    }
  }, []);

  const trackInteraction = (
    interactionType: string,
    elementName: string,
    properties: Record<string, string | number> = {},
  ): void => {
    if (auth?.access_token) {
      TrackAnalyticsEvent(
        AnalyticsEventNames.interaction,
        auth?.access_token,
        auth?.user_id,
        {
          interaction_type: interactionType,
          element_name: elementName,
          ...MODAL_ANALYTICS_CONTEXT,
          ...properties,
        },
      );
    }
  };

  const trackButtonClick = (
    buttonText: string,
    elementName: string,
    properties: Record<string, string | number> = {},
  ): void => {
    trackInteraction("button_click", elementName, {
      button_text: buttonText,
      ...properties,
    });
  };

  const updateRecipient = (
    id: number,
    update: Partial<RecipientDraft>,
  ): void => {
    setResponseMessage("");
    setRecipients((current) =>
      current.map((recipient) =>
        recipient.id === id ? { ...recipient, ...update } : recipient,
      ),
    );
  };

  const addRecipient = (): void => {
    if (recipients.length >= MAX_SHARE_FETAL_SEX_RECIPIENTS) {
      return;
    }
    setResponseMessage("");
    trackButtonClick("Recipient", "add_share_fetal_sex_recipient", {
      recipient_count: recipients.length + 1,
    });
    setRecipients((current) =>
      current.length >= MAX_SHARE_FETAL_SEX_RECIPIENTS
        ? current
        : [...current, createEmptyRecipient()],
    );
  };

  const removeRecipient = (id: number): void => {
    setResponseMessage("");
    setRecipients((current) => {
      if (current.length <= 1) {
        return current;
      }
      trackButtonClick("Remove recipient", "remove_share_fetal_sex_recipient", {
        recipient_count: current.length - 1,
      });
      return current.filter((recipient) => recipient.id !== id);
    });
  };

  const submitShare = async (): Promise<void> => {
    if (isLoading) {
      return;
    }
    trackButtonClick("Send", "submit_share_fetal_sex", {
      recipient_count: recipients.length,
    });
    setResponseMessage("");
    let hasError = false;
    const validatedRecipients = recipients.map((recipient) => {
      let nameError = "";
      if (!recipient.name.trim()) {
        nameError = t("fetalSex.standalone.share.errors.required");
      } else if (
        recipient.name.trim().length > MAX_SHARE_FETAL_SEX_RECIPIENT_NAME_LENGTH
      ) {
        nameError = t("fetalSex.standalone.share.errors.nameTooLong");
      } else if (!validateName(recipient.name.trim())) {
        nameError = t("fetalSex.standalone.share.errors.invalidName");
      }
      let emailError = "";
      if (!recipient.email.trim()) {
        emailError = t("fetalSex.standalone.share.errors.required");
      } else if (!validateEmail(recipient.email.trim())) {
        emailError = t("fetalSex.standalone.share.errors.invalidEmail");
      }
      if (nameError || emailError) {
        hasError = true;
      }
      return { ...recipient, emailError, nameError };
    });
    setRecipients(validatedRecipients);
    if (hasError) {
      return;
    }

    setIsLoading(true);
    onRequestInFlightChange(true);
    try {
      const response = await ShareFetalSexEmailRequest(
        {
          req_identifier: requisitionID,
          recipients: recipients.map((recipient) => ({
            to_email: recipient.email.trim(),
            to_name: recipient.name.trim(),
          })),
        },
        auth?.access_token,
      );
      // 201 = every email sent, 207 = partial success; both show the success
      // state with the emails that went out.
      if (response.status === 201 || response.status === 207) {
        const body = (await response.json()) as ShareFetalSexEmailResponse;
        const sentCount = body.sent_emails.length;
        const failedCount = body.failed_emails?.length ?? 0;
        trackInteraction("request_result", "share_fetal_sex_completed", {
          outcome: failedCount > 0 ? "partial" : "success",
          recipient_count: recipients.length,
          sent_count: sentCount,
          failed_count: failedCount,
          response_status: response.status,
        });
        setSentEmails(body.sent_emails);
        setPhase("success");
        if (body.failed_emails?.length) {
          setResponseMessage(
            t("fetalSex.standalone.share.errors.partial", {
              emails: body.failed_emails.join(", "),
            }),
          );
        }
      } else {
        trackInteraction("request_result", "share_fetal_sex_failed", {
          failure_type: "http",
          recipient_count: recipients.length,
          response_status: response.status,
        });
        let failedRecipientCount: number | undefined;
        try {
          const body =
            (await response.json()) as Partial<ShareFetalSexEmailResponse>;
          failedRecipientCount = body.failed_emails?.length;
        } catch {
          // The status is still useful when the server does not return JSON.
        }
        console.error("Failed to share standalone fetal sex", {
          failedRecipientCount,
          responseStatus: response.status,
        });
        setResponseMessage(t("fetalSex.standalone.share.errors.generic"));
      }
    } catch (error) {
      trackInteraction("request_result", "share_fetal_sex_failed", {
        failure_type: "network",
        recipient_count: recipients.length,
      });
      console.error("Failed to share standalone fetal sex", {
        error,
        failureType: "network",
      });
      setResponseMessage(t("fetalSex.standalone.share.errors.network"));
    } finally {
      setIsLoading(false);
      onRequestInFlightChange(false);
    }
  };

  const addMorePeople = (): void => {
    trackButtonClick("Add people", "add_people_share_fetal_sex");
    setRecipients([createEmptyRecipient()]);
    setSentEmails([]);
    setResponseMessage("");
    // The authorization checkbox stays checked: the patient already consented
    // in this session.
    setPhase("form");
  };

  const hasMaxRecipients = recipients.length >= MAX_SHARE_FETAL_SEX_RECIPIENTS;
  // Keep Send available after authorization so submitShare can display the
  // existing per-field validation errors before deciding whether to send.
  const canSubmit = isAuthorized;

  const successContent = (
    <>
      <StandaloneFetalSexShareSuccessIcon>
        <FontAwesomeIcon icon={faCircleCheck} />
      </StandaloneFetalSexShareSuccessIcon>
      <StandaloneFetalSexShareSuccessMessage>
        {GetTypographyContent({
          content: (
            <>
              <b>{t("fetalSex.standalone.share.successTitle")}</b>
              {`  ${t("fetalSex.standalone.share.successMessage")}`}
            </>
          ),
          size: "medium",
          textColor: "greyBody",
        })}
        {sentEmails.map((email) => (
          <div key={email}>
            {GetTypographyContent({
              content: email,
              size: "medium",
              textColor: "greyBody",
              isBold: true,
            })}
          </div>
        ))}
      </StandaloneFetalSexShareSuccessMessage>
      <StandaloneFetalSexShareSuccessActionRow>
        <StandaloneFetalSexShareTextButton
          type="button"
          onClick={addMorePeople}
        >
          {t("fetalSex.standalone.share.addPeople")}
        </StandaloneFetalSexShareTextButton>
        <StandaloneFetalSexShareTextButton
          type="button"
          onClick={() => {
            trackButtonClick("Done", "done_share_fetal_sex");
            onClose();
          }}
        >
          {t("fetalSex.standalone.share.done")}
        </StandaloneFetalSexShareTextButton>
      </StandaloneFetalSexShareSuccessActionRow>
    </>
  );

  const formContent = (
    <>
      <StandaloneFetalSexShareModalHeader>
        {GetTypographyContent({
          content: t("fetalSex.standalone.share.title"),
          size: "h3",
          textColor: "greyHeading",
          isBold: true,
        })}
        {GetTypographyContent({
          content: t("fetalSex.standalone.share.instructions"),
          size: "medium",
          textColor: "greyBody",
        })}
      </StandaloneFetalSexShareModalHeader>
      {recipients.map((recipient, index) => (
        <StandaloneFetalSexShareRecipientGroup key={recipient.id}>
          <StandaloneFetalSexShareRecipientHeader>
            {GetTypographyContent({
              content: t("fetalSex.standalone.share.recipient", {
                number: index + 1,
              }),
              size: "small",
              textColor: "greyBody",
            })}
            {recipients.length > 1 && (
              <StandaloneFetalSexShareRemoveButton
                aria-label={t("fetalSex.standalone.share.removeRecipient", {
                  number: index + 1,
                })}
                type="button"
                onClick={() => removeRecipient(recipient.id)}
              >
                <FontAwesomeIcon icon={faXmark} />
              </StandaloneFetalSexShareRemoveButton>
            )}
          </StandaloneFetalSexShareRecipientHeader>
          <StandaloneFetalSexShareRecipientFields>
            <TextField
              label={t("fetalSex.standalone.share.nameLabel")}
              placeholder={t("fetalSex.standalone.share.namePlaceholder")}
              size="small"
              value={recipient.name}
              error={recipient.nameError}
              onChange={(e) =>
                updateRecipient(recipient.id, {
                  name: e.target.value,
                  nameError: "",
                })
              }
            />
            <TextField
              label={t("fetalSex.standalone.share.emailLabel")}
              placeholder={t("fetalSex.standalone.share.emailPlaceholder")}
              size="small"
              value={recipient.email}
              error={recipient.emailError}
              onChange={(e) =>
                updateRecipient(recipient.id, {
                  email: e.target.value,
                  emailError: "",
                })
              }
            />
          </StandaloneFetalSexShareRecipientFields>
        </StandaloneFetalSexShareRecipientGroup>
      ))}
      <StandaloneFetalSexShareAddRecipientRow>
        <StandaloneFetalSexShareTextButton
          type="button"
          disabled={hasMaxRecipients}
          onClick={addRecipient}
        >
          <FontAwesomeIcon icon={faPlus} />
          {t("fetalSex.standalone.share.addRecipient")}
        </StandaloneFetalSexShareTextButton>
      </StandaloneFetalSexShareAddRecipientRow>
      <StandaloneFetalSexShareAuthorizationRow>
        <Checkbox
          id={SHARE_AUTHORIZATION_ID}
          name="shareAuthorization"
          data-testid="share-fetal-sex-authorization"
          checked={isAuthorized}
          onChange={(e) => {
            setIsAuthorized(e.target.checked);
            trackInteraction("checkbox_change", "authorize_share_fetal_sex", {
              authorization_state: e.target.checked ? "checked" : "unchecked",
            });
          }}
          sx={{
            color: themePalette.greyLight,
            padding: 0,
            "& .MuiSvgIcon-root": { fontSize: 24 },
            "&.Mui-checked": { color: themePalette.redMedium },
          }}
        />
        <StandaloneFetalSexShareAuthorizationText>
          <label htmlFor={SHARE_AUTHORIZATION_ID}>
            {GetTypographyContent({
              content: t("fetalSex.standalone.share.authorization"),
              size: "small",
              textColor: "greyBody",
            })}
          </label>
          {GetTypographyContent({
            content: t(
              isMobile
                ? "fetalSex.standalone.share.privacyLinkMobile"
                : "fetalSex.standalone.share.privacyLinkDesktop",
            ),
            size: "small",
            textColor: "greenDark",
            link: PRIVACY_PRACTICES_URL,
            externalLink: true,
            onLinkClick: () =>
              trackInteraction(
                "link_click",
                "share_fetal_sex_privacy_practices",
              ),
          })}
        </StandaloneFetalSexShareAuthorizationText>
      </StandaloneFetalSexShareAuthorizationRow>
      <StandaloneFetalSexShareActionRow>
        <StandaloneFetalSexShareTextButton
          type="button"
          disabled={isLoading}
          onClick={() => {
            trackButtonClick("Cancel", "cancel_share_fetal_sex");
            onClose();
          }}
        >
          {t("fetalSex.standalone.share.cancel")}
        </StandaloneFetalSexShareTextButton>
        <StandaloneFetalSexShareSubmitButton
          aria-label={t("fetalSex.standalone.share.send")}
          aria-busy={isLoading}
          type="button"
          disabled={isLoading || !canSubmit}
          onClick={submitShare}
        >
          {isLoading ? (
            <FontAwesomeIcon
              aria-hidden="true"
              data-testid="share-fetal-sex-send-spinner"
              icon={faSpinner}
              spin
            />
          ) : (
            t("fetalSex.standalone.share.send")
          )}
        </StandaloneFetalSexShareSubmitButton>
      </StandaloneFetalSexShareActionRow>
    </>
  );

  return (
    <StandaloneFetalSexShareModalCard
      data-testid="standalone-fetal-sex-share-modal"
      deviceType={deviceType}
      ref={ref}
      tabIndex={-1}
    >
      {phase === "success" ? successContent : formContent}
      {responseMessage && (
        <StandaloneFetalSexShareError role="alert">
          {GetTypographyContent({
            content: responseMessage,
            size: "small",
            textColor: "redDark",
          })}
        </StandaloneFetalSexShareError>
      )}
    </StandaloneFetalSexShareModalCard>
  );
});
